import React, { useState, useEffect, useRef } from 'react';
import { Camera, Mic, Edit3, Trash2, RotateCcw, Check, ChevronDown, Apple, Mail, ArrowRight, Star, Users, Zap, GalleryHorizontal, Share2 } from 'lucide-react';

export default function BetaLandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('ios');
  // 'idle' | 'loading' | 'success'
  const [status, setStatus] = useState(() => {
    try { return localStorage.getItem('pv_submitted') === 'true' ? 'success' : 'idle'; } catch { return 'idle'; }
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [consent, setConsent] = useState(false);
  const [betaCount] = useState(1247);
  const [formKey, setFormKey] = useState(0);
  const emailRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Read from ref to handle browser autofill, which can bypass React onChange
    const refValue = emailRef.current ? emailRef.current.value : '';
    const stateValue = email;
    const submittedEmail = (refValue || stateValue).trim();
    if (!submittedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submittedEmail)) {
      setError('Please enter a valid email address.');
      setInfo('');
      return;
    }
    if (!consent) {
      setError('Please agree to the Terms of Use and Privacy Policy to continue.');
      setInfo('');
      return;
    }
    // Check for duplicate email + platform combination
    const signupKey = `${submittedEmail.toLowerCase()}:${platform}`;
    let knownSignups = [];
    try { knownSignups = JSON.parse(localStorage.getItem('pv_signups') || '[]'); } catch {}
    if (knownSignups.includes(signupKey)) {
      const platformLabel = platform === 'ios' ? 'iOS' : 'Android';
      setError('');
      setInfo(`${submittedEmail} is already signed up for ${platformLabel}.`);
      return;
    }
    // Sync React state in case autofill skipped onChange
    if (submittedEmail !== email) setEmail(submittedEmail);
    setError('');
    setInfo('');
    setStatus('loading');
    // Submit to Netlify Forms — redirect:manual prevents page navigation
    try {
      const formData = new FormData();
      formData.append('email', submittedEmail);
      formData.append('platform', platform);
      formData.append('form-name', 'beta-signup');
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
        redirect: 'manual',
      });
    } catch (err) {
      console.error('Netlify Forms error:', err);
    }
    // Send welcome email — failure here does not block success screen
    try {
      await fetch('/.netlify/functions/send-welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: submittedEmail, platform }),
      });
    } catch (err) {
      console.error('Email send error:', err);
    }
    try {
      localStorage.setItem('pv_submitted', 'true');
      const signupKey = `${submittedEmail.toLowerCase()}:${platform}`;
      let knownSignups = [];
      try { knownSignups = JSON.parse(localStorage.getItem('pv_signups') || '[]'); } catch {}
      if (!knownSignups.includes(signupKey)) {
        knownSignups.push(signupKey);
        localStorage.setItem('pv_signups', JSON.stringify(knownSignups));
      }
    } catch {}
    setStatus('success');
  };

  const features = [
    {
      icon: <Camera className="w-6 h-6" />,
      title: 'Instant Photo Capture',
      description: 'Take stunning photos and add voice context to each shot the moment it happens.',
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: 'Voice Note Recording',
      description: 'Record your thoughts and memories immediately after capturing the perfect shot.',
    },
    {
      icon: <RotateCcw className="w-6 h-6" />,
      title: 'Re-record Anytime',
      description: 'Made a mistake? Re-record your voice notes as many times as you need.',
    },
    {
      icon: <Edit3 className="w-6 h-6" />,
      title: 'Edit Descriptions',
      description: 'Add or modify text descriptions for better organisation and searchability.',
    },
    {
      icon: <Trash2 className="w-6 h-6" />,
      title: 'Easy Management',
      description: 'Remove photos and voice notes you no longer need with intuitive controls.',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Lightning Fast',
      description: 'Optimised for speed so you never miss the perfect moment to record.',
    },
    {
      icon: <GalleryHorizontal className="w-6 h-6" />,
      title: 'Gallery Voice Notes',
      description: 'Select any existing photo from your gallery and attach a voice note to it — not just newly captured shots.',
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: 'Export & Share',
      description: 'Export your photos together with their voice notes as a package to share with friends, family, or clients.',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah K.',
      role: 'Travel Photographer',
      text: 'PhotoVoice changed how I document my travels. I can capture a photo and immediately narrate the story behind it.',
      stars: 5,
    },
    {
      name: 'James M.',
      role: 'Journalist',
      text: 'An essential tool for my fieldwork. Voice notes attached to photos make my reporting so much richer.',
      stars: 5,
    },
    {
      name: 'Priya L.',
      role: 'Memory Keeper',
      text: 'I use it to capture family moments with voice stories. My kids will treasure these recordings someday.',
      stars: 5,
    },
  ];

  return (
    <div
      style={{
        background: 'linear-gradient(to bottom, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
        minHeight: '100vh',
        color: '#fff',
        fontFamily: "'Work Sans', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Work+Sans:wght@300;400;500;600&display=swap');

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out forwards; }
        .stagger-1 { animation-delay: 0.1s; opacity: 0; }
        .stagger-2 { animation-delay: 0.2s; opacity: 0; }
        .stagger-3 { animation-delay: 0.3s; opacity: 0; }
        .stagger-4 { animation-delay: 0.4s; opacity: 0; }
        .stagger-5 { animation-delay: 0.5s; opacity: 0; }

        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          border: none;
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .feature-card {
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          border-radius: 24px;
          padding: 32px;
        }

        .feature-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(102, 126, 234, 0.5);
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(102, 126, 234, 0.2);
        }

        .testimonial-card {
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          padding: 28px;
          transition: all 0.3s ease;
        }

        .testimonial-card:hover {
          border-color: rgba(102, 126, 234, 0.4);
          transform: translateY(-3px);
        }

        .email-input {
          background: rgba(255, 255, 255, 0.07);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 50px;
          color: white;
          padding: 16px 24px;
          font-size: 16px;
          font-family: 'Work Sans', sans-serif;
          outline: none;
          transition: all 0.3s ease;
          width: 100%;
        }

        .email-input::placeholder { color: rgba(255,255,255,0.35); }

        .email-input:focus {
          border-color: rgba(102, 126, 234, 0.7);
          background: rgba(255, 255, 255, 0.1);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
        }

        .platform-btn {
          padding: 10px 20px;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          font-family: 'Work Sans', sans-serif;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .platform-btn.active {
          background: white;
          color: #667eea;
        }

        .platform-btn.inactive {
          background: transparent;
          color: white;
        }

        .platform-btn.inactive:hover {
          background: rgba(255,255,255,0.05);
        }

        .scroll-indicator { animation: float 2s ease-in-out infinite; }

        /* Mobile responsive */
        @media (max-width: 640px) {
          .nav-links { display: none; }
          .stats-row { flex-direction: column; gap: 16px; }
          .stats-divider { display: none; }
          .email-form-row { flex-direction: column; }
          .perks-grid { grid-template-columns: 1fr !important; }
          .footer-inner { flex-direction: column; align-items: flex-start; gap: 16px; }
        }

        .noise-overlay {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          opacity: 0.03;
          pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }

        .success-box {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
          border: 1px solid rgba(102, 126, 234, 0.4);
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>

      <div className="noise-overlay" />

      {/* Navigation */}
      <nav
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 50,
          padding: '16px 24px',
          backdropFilter: 'blur(10px)',
          background: scrollY > 50 ? 'rgba(10,10,10,0.9)' : 'transparent',
          borderBottom: scrollY > 50 ? '1px solid rgba(255,255,255,0.1)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Camera size={20} color="white" />
            </div>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700 }}>PhotoVoice</span>
          </div>

          <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            <a href="#features" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14, transition: 'color 0.2s' }}
              onMouseOver={e => e.target.style.color = '#fff'}
              onMouseOut={e => e.target.style.color = '#9ca3af'}>Features</a>
            <a href="#testimonials" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14, transition: 'color 0.2s' }}
              onMouseOver={e => e.target.style.color = '#fff'}
              onMouseOut={e => e.target.style.color = '#9ca3af'}>Reviews</a>
            <a href="#signup" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14, transition: 'color 0.2s' }}
              onMouseOver={e => e.target.style.color = '#fff'}
              onMouseOut={e => e.target.style.color = '#9ca3af'}>Beta Access</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 0' }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <div className="animate-float" style={{ position: 'absolute', top: 80, left: 40, width: 288, height: 288, background: '#7c3aed', borderRadius: '50%', opacity: 0.15, filter: 'blur(60px)' }} />
          <div className="animate-float" style={{ position: 'absolute', bottom: 80, right: 40, width: 384, height: 384, background: '#4338ca', borderRadius: '50%', opacity: 0.15, filter: 'blur(60px)', animationDelay: '1s' }} />
        </div>

        <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          {/* Beta badge */}
          <div className="animate-fadeInUp" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 20px', borderRadius: 50,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            marginBottom: 32,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 8px #4ade80' }} />
            <span style={{ fontSize: 14, color: '#a78bfa' }}>Now accepting beta testers</span>
          </div>

          <h1 className="animate-fadeInUp stagger-1" style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(48px, 8vw, 88px)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: 24,
          }}>
            Capture moments<br />
            <span className="gradient-text">with your voice</span>
          </h1>

          <p className="animate-fadeInUp stagger-2" style={{ fontSize: 20, color: '#9ca3af', maxWidth: 600, margin: '0 auto 48px', lineHeight: 1.7 }}>
            PhotoVoice lets you add voice notes to your photos instantly. Be among the first to experience the future of photo storytelling.
          </p>

          {/* Stats row */}
          <div className="animate-fadeInUp stagger-3 stats-row" style={{ display: 'flex', justifyContent: 'center', gap: 48, marginBottom: 48 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 32, fontWeight: 700 }}>{betaCount.toLocaleString()}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Beta testers</div>
            </div>
            <div className="stats-divider" style={{ width: 1, background: 'rgba(255,255,255,0.1)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 32, fontWeight: 700 }}>iOS & Android</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Platforms</div>
            </div>
            <div className="stats-divider" style={{ width: 1, background: 'rgba(255,255,255,0.1)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 32, fontWeight: 700 }}>Free</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>During beta</div>
            </div>
          </div>

          <button
            className="animate-fadeInUp stagger-4 btn-primary"
            style={{ padding: '18px 40px', borderRadius: 50, fontSize: 18, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 10 }}
            onClick={() => document.getElementById('signup').scrollIntoView({ behavior: 'smooth' })}
          >
            <Users size={20} />
            Join the Beta — It's Free
          </button>

          <div className="scroll-indicator" style={{ marginTop: 80 }}>
            <ChevronDown size={32} style={{ margin: '0 auto', color: '#4b5563' }} />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: '120px 24px', position: 'relative' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 700, marginBottom: 16 }}>
              Powerful Features
            </h2>
            <p style={{ fontSize: 18, color: '#6b7280', maxWidth: 600, margin: '0 auto' }}>
              Everything you need to capture and document your moments with voice and photos
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {features.map((feature, i) => (
              <div key={i} className="feature-card">
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'linear-gradient(135deg, #8b5cf6, #4f46e5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 24,
                }}>
                  {feature.icon}
                </div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, marginBottom: 12 }}>
                  {feature.title}
                </h3>
                <p style={{ color: '#9ca3af', lineHeight: 1.7 }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" style={{ padding: '120px 24px', position: 'relative' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 700, marginBottom: 16 }}>
              What Beta Testers Say
            </h2>
            <p style={{ fontSize: 18, color: '#6b7280' }}>Early feedback from our closed beta</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {testimonials.map((t, i) => (
              <div key={i} className="testimonial-card">
                <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                  {[...Array(t.stars)].map((_, j) => (
                    <Star key={j} size={16} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>
                <p style={{ color: '#d1d5db', lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic' }}>"{t.text}"</p>
                <div>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Signup */}
      <section id="signup" style={{ padding: '120px 24px', position: 'relative' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ marginBottom: 48 }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 700, marginBottom: 16 }}>
              Join the Beta
            </h2>
            <p style={{ fontSize: 18, color: '#9ca3af', lineHeight: 1.7 }}>
              Get early access to PhotoVoice — completely free during beta. Limited spots available.
            </p>
          </div>

          <div key={status}>
          {status === 'success' ? (
            <div className="success-box">
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 24px',
              }}>
                <Check size={32} color="white" />
              </div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 700, marginBottom: 12 }}>
                You're on the list!
              </h3>
              <p style={{ color: '#9ca3af', lineHeight: 1.7 }}>
                Thanks for signing up. We'll email you as soon as your beta access is ready.
              </p>
              <button
                onClick={() => {
                  try { localStorage.removeItem('pv_submitted'); } catch {}
                  setStatus('idle');
                  setEmail('');
                  setError('');
                  setInfo('');
                  setConsent(false);
                  setPlatform('ios');
                  setFormKey(k => k + 1);
                }}
                style={{
                  marginTop: 24,
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#9ca3af',
                  borderRadius: 50,
                  padding: '10px 24px',
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, color 0.2s',
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#fff'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = '#9ca3af'; }}
              >
                Sign up with a different email
              </button>
            </div>
          ) : (
            <form
              key={formKey}
              name="beta-signup"
              data-netlify="true"
              noValidate
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <input type="hidden" name="form-name" value="beta-signup" />
              <input type="hidden" name="platform" value={platform} />

              {/* Platform selector */}
              <div style={{
                display: 'inline-flex', gap: 8, padding: 6,
                background: 'rgba(255,255,255,0.07)', borderRadius: 50,
                margin: '0 auto',
              }}>
                <button
                  type="button"
                  className={`platform-btn ${platform === 'ios' ? 'active' : 'inactive'}`}
                  onClick={() => setPlatform('ios')}
                >
                  <Apple size={16} /> iOS
                </button>
                <button
                  type="button"
                  className={`platform-btn ${platform === 'android' ? 'active' : 'inactive'}`}
                  onClick={() => setPlatform('android')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C14.15 1.23 13.11 1 12 1c-1.12 0-2.15.23-3.09.63L7.43.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.3 1.3C6.34 3.26 5 5.51 5 8h14c0-2.49-1.34-4.74-3.47-5.84zM10 6H9V5h1v1zm5 0h-1V5h1v1z"/>
                  </svg>
                  Android
                </button>
              </div>

              <div className="email-form-row" style={{ display: 'flex', gap: 12 }}>
                <input
                  ref={emailRef}
                  type="email"
                  name="email"
                  autoComplete="email"
                  className="email-input"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={e => { if (e.target.value && e.target.value !== email) setEmail(e.target.value); }}
                />
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={status === 'loading'}
                  style={{ padding: '16px 28px', borderRadius: 50, fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  {status === 'loading' ? 'Joining...' : (
                    <>Join Beta <ArrowRight size={18} /></>
                  )}
                </button>
              </div>

              {/* Consent checkbox */}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                cursor: 'pointer', textAlign: 'left',
              }}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  style={{
                    marginTop: 2, width: 18, height: 18,
                    accentColor: '#667eea', flexShrink: 0, cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6 }}>
                  I have read and agree to the{' '}
                  <a href="/terms" style={{ color: '#a78bfa', textDecoration: 'underline' }}>Terms of Use</a>
                  {' '}and{' '}
                  <a href="/privacy" style={{ color: '#a78bfa', textDecoration: 'underline' }}>Privacy Policy</a>.
                </span>
              </label>

              {error && (
                <p style={{ color: '#f87171', fontSize: 14 }}>{error}</p>
              )}
              {info && (
                <p style={{ color: '#a78bfa', fontSize: 14 }}>{info} You're already on the list!</p>
              )}

              <p style={{ color: '#4b5563', fontSize: 13 }}>
                No spam. No credit card required. Unsubscribe anytime.
              </p>
            </form>
          )}
          </div>

          {/* Perks */}
          <div className="perks-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 48 }}>
            {[
              { icon: <Zap size={18} />, text: 'Early access' },
              { icon: <Mail size={18} />, text: 'Beta updates' },
              { icon: <Check size={18} />, text: 'Free forever tier' },
            ].map((perk, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 16px', borderRadius: 12,
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                color: '#9ca3af', fontSize: 14,
              }}>
                <span style={{ color: '#a78bfa' }}>{perk.icon}</span>
                {perk.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '48px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="footer-inner" style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Camera size={18} color="white" />
            </div>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700 }}>PhotoVoice</span>
          </div>

          <div style={{ display: 'flex', gap: 24 }}>
            <a href="/privacy" style={{ color: '#4b5563', fontSize: 13, textDecoration: 'none' }}
              onMouseOver={e => e.target.style.color = '#9ca3af'}
              onMouseOut={e => e.target.style.color = '#4b5563'}>Privacy Policy</a>
            <a href="/terms" style={{ color: '#4b5563', fontSize: 13, textDecoration: 'none' }}
              onMouseOver={e => e.target.style.color = '#9ca3af'}
              onMouseOut={e => e.target.style.color = '#4b5563'}>Terms of Use</a>
            <a href="mailto:support@photovoice.app" style={{ color: '#4b5563', fontSize: 13, textDecoration: 'none' }}
              onMouseOver={e => e.target.style.color = '#9ca3af'}
              onMouseOut={e => e.target.style.color = '#4b5563'}>Contact</a>
          </div>

          <p style={{ color: '#374151', fontSize: 13 }}>© 2026 PhotoVoice. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
